
import { useState, useCallback, useEffect } from 'react';
import { Insumo, UnidadeMedida } from '../types';
import { supabaseService } from '../supabaseService';

export const useInsumos = (empresaId?: string) => {
  const [insumos, setInsumos] = useState<Insumo[]>([]);
  const [unidades, setUnidades] = useState<UnidadeMedida[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInsumos = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await supabaseService.getInsumos(empresaId);
      setInsumos(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  const loadUnidades = useCallback(async () => {
    try {
      const data = await supabaseService.getUnidadesMedida();
      setUnidades(data);
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const saveInsumo = async (insumo: Partial<Insumo>) => {
    setLoading(true);
    try {
      await supabaseService.salvarInsumo({ ...insumo, empresa_id: empresaId });
      await loadInsumos();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteInsumo = async (id: string) => {
    setLoading(true);
    try {
      await supabaseService.deletarInsumo(id);
      await loadInsumos();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const saveUnidade = async (unidade: Partial<UnidadeMedida>) => {
    setLoading(true);
    try {
      const data = await supabaseService.salvarUnidadeMedida(unidade);
      await loadUnidades();
      return data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresaId) {
      loadInsumos();
      loadUnidades();
    }
  }, [empresaId, loadInsumos, loadUnidades]);

  return {
    insumos,
    unidades,
    loading,
    error,
    reload: loadInsumos,
    saveInsumo,
    deleteInsumo,
    saveUnidade,
    reloadUnidades: loadUnidades
  };
};
